/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: IntersectionObserver hook for scroll-reveal animations on brand pages
import { useEffect, useRef, useState, type RefObject } from 'react';

interface UseInViewOptions {
    once?: boolean;
    margin?: string;
}

export function useInView<T extends HTMLElement = HTMLElement>({
    once = true,
    margin = '0px',
}: UseInViewOptions = {}): { ref: RefObject<T | null>; inView: boolean } {
    const ref = useRef<T>(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (once) observer.disconnect();
                } else if (!once) {
                    setInView(false);
                }
            },
            { rootMargin: margin }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [once, margin]);

    return { ref, inView };
}
